import { useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import './i18n.ts';
import Sidebar from './components/Sidebar.tsx';
import Header from './components/Header.tsx';
import CompanyList from './pages/CompanyList.tsx';
import CompanyForm from './pages/CompanyForm.tsx';
import LicenseList from './pages/LicenseList.tsx';
import LicenseForm from './pages/LicenseForm.tsx';
import ExpenseList from './pages/ExpenseList.tsx';
import ExpenseForm from './pages/ExpenseForm.tsx';
import Login from './pages/Login.tsx';
import Dashboard from './pages/Dashboard.tsx';
import UserManagement from './pages/UserManagement.tsx';

interface User {
  id: number;
  username: string;
  name: string;
  nickname?: string;
  displayName?: string;
  email?: string;
  role?: string;
}

function MainLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      // Prefer currentUserName from OME login (nickname)
      const currentUserName = localStorage.getItem('currentUserName');
      if (currentUserName && !userData.displayName) {
        userData.displayName = currentUserName;
      }
      return userData;
    }
    return null;
  });

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('currentUserName');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('external_token');
    setUser(null);
    window.location.href = '/login';
  };

  const currentUser: User | null = user;

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} role={currentUser.role || 'operator'} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <Header onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} user={currentUser} onLogout={handleLogout} />
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/', element: <Navigate to="/dashboard" /> },
  { path: '/dashboard', element: <MainLayout><Dashboard /></MainLayout> },
  { path: '/company', element: <MainLayout><CompanyList /></MainLayout> },
  { path: '/company/new', element: <MainLayout><CompanyForm /></MainLayout> },
  { path: '/company/edit/:id', element: <MainLayout><CompanyForm /></MainLayout> },
  { path: '/license', element: <MainLayout><LicenseList /></MainLayout> },
  { path: '/license/new', element: <MainLayout><LicenseForm /></MainLayout> },
  { path: '/license/edit/:id', element: <MainLayout><LicenseForm /></MainLayout> },
  { path: '/expense', element: <MainLayout><ExpenseList /></MainLayout> },
  { path: '/expense/new', element: <MainLayout><ExpenseForm /></MainLayout> },
  { path: '/expense/edit/:id', element: <MainLayout><ExpenseForm /></MainLayout> },
  { path: '/users', element: <MainLayout><UserManagement /></MainLayout> },
  { path: '/alert', element: <Navigate to="/license?view=renewal" /> },
  { path: '/expiration-alert', element: <Navigate to="/license?view=renewal" /> },
  { path: '*', element: <Navigate to="/" /> },
]);

export default function App() {
  return <RouterProvider router={router} />;
}